import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MdList, MdApps, MdSync } from 'react-icons/md';
import { FiUser, FiAlertCircle, FiHelpCircle } from 'react-icons/fi';
import { SidebarItem, MenuAccordionItem } from '../SidebarComponents';

// API URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const ClientsMenu = () => {
  const location = useLocation();
  const [pendingHelpRequests, setPendingHelpRequests] = useState(0);
  const [pendingCancellations, setPendingCancellations] = useState(0);
  const [pendingRejects, setPendingRejects] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState({
    helpResponse: null,
    cancellationResponse: null,
    rejectResponse: null,
    error: null
  });

  // Fetch user help requests count
  const fetchHelpRequests = async () => {
    const userToken_TOVIAPPS = localStorage.getItem('userToken_TOVIAPPS');
    if (!userToken_TOVIAPPS) return { count: 0 };
    
    const url = `${API_BASE_URL}api/menu/requests-status/user-help`;   
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${userToken_TOVIAPPS}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) throw new Error(`Failed to fetch user help requests: ${response.status}`);
    const data = await response.json();
    return data;
  };

  // Fetch user cancellation requests count
  const fetchCancellations = async () => {
    const userToken_TOVIAPPS = localStorage.getItem('userToken_TOVIAPPS');
    if (!userToken_TOVIAPPS) return { count: 0 };
    
    const url = `${API_BASE_URL}api/menu/requests-status/user-cancellation`;    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${userToken_TOVIAPPS}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) throw new Error(`Failed to fetch user cancellations: ${response.status}`);
    const data = await response.json();
    return data;
  };

  // Fetch reject confirmations count
  const fetchRejects = async () => {
    const userToken_TOVIAPPS = localStorage.getItem('userToken_TOVIAPPS');
    if (!userToken_TOVIAPPS) return { count: 0 };
    
    const url = `${API_BASE_URL}api/menu/requests-status/reject`;    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${userToken_TOVIAPPS}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) throw new Error(`Failed to fetch reject confirmations: ${response.status}`);
    const data = await response.json();
    return data;
  };

  // Fetch request counts from API
  const fetchCounts = async () => {
    try {
      setIsLoading(true);
      
      // Fetch all counts
      const helpResult = await fetchHelpRequests();
      const cancellationResult = await fetchCancellations();
      const rejectResult = await fetchRejects();
     
      // Set state with counts
      setPendingHelpRequests(helpResult.count || helpResult?.userHelpConfirmation?.total || 0);
      setPendingCancellations(cancellationResult.count || cancellationResult?.userCancellationRequest?.total || 0);
      setPendingRejects(rejectResult.count || rejectResult?.rejectConfirmation?.total || 0);
      
    } catch (error) {
      console.error('Error fetching request counts:', error);
      // Set defaults in case of error
      setPendingHelpRequests(0);
      setPendingCancellations(0);
      setPendingRejects(0);
    } finally {
      setIsLoading(false);
    }
  };
  
  const hasFetched = useRef(false);

  // Initial fetch on component mount
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    fetchCounts();
    
    // Set up an interval to refresh the counts every 5 minutes
    const intervalId = setInterval(fetchCounts, 5 * 60 * 1000);
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);
  
  // Calculate total pending count
  const pendings = pendingHelpRequests + pendingCancellations + pendingRejects;
  
  // Check if current route is active
  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  // Check if an accordion is active based on subroutes
  const isAccordionActive = (parentPath) => {
    return location.pathname.startsWith(parentPath);
  };

  // Only show debug in development
  const showDebug = process.env.NODE_ENV === 'development';

  return (
    <>
      <MenuAccordionItem
        icon={FiUser}
        label="Contratantes"
        isActive={isAccordionActive('/contratantes')}
        hasBadge={true}
        badgeCount={isLoading ? '...' : pendings}
        defaultIsOpen={isAccordionActive('/contratantes')}
      >
        <SidebarItem 
          icon={MdList} 
          label="Lista" 
          to="/contratantes/list" 
          active={isActive('/contratantes/list')} 
          isSubmenuItem
        />
        <SidebarItem 
          icon={FiAlertCircle} 
          label="Cancelamentos" 
          to="/contratantes/cancelamento" 
          active={isActive('/contratantes/cancelamento')} 
          isSubmenuItem
          hasBadge={true}
          badgeCount={isLoading ? '...' : pendingCancellations}
        />
        <SidebarItem
          icon={MdApps} 
          label="Recusa de Serviços" 
          to="/contratantes/recusa-servicos" 
          active={isActive('/contratantes/recusa-servicos')} 
          isSubmenuItem
          hasBadge={true}
          badgeCount={isLoading ? '...' : pendingRejects}
        />
        <SidebarItem 
          icon={FiHelpCircle} 
          label="Preciso de Ajuda" 
          to="/contratantes/preciso-ajuda" 
          active={isActive('/contratantes/preciso-ajuda')} 
          isSubmenuItem
          hasBadge={true}
          badgeCount={isLoading ? '...' : pendingHelpRequests}
        />
      </MenuAccordionItem>
    </>
  );
};

export default ClientsMenu;